import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import config from 'config';
import withShopContext from 'core/hocs/withShopContext';
import withPageLayout from 'core/hocs/withPageLayout';
import withDescriptionData from 'core/hocs/withDescriptionData';
import { SimpleVariantFragmentType } from 'types/Variant';
import { Article as ArticleType, SimilarArticle } from 'types/models/Article';
import ArticlePage, { getBreadcrumbs } from '@pages/Article';
import { GetPageLayoutResult, serverRequests as pageLayoutServerRequests } from '@layouts/PageLayout/PageLayout.utils';
import { articleRequestConfig, GetArticleResult, GetArticleVariables } from 'graphql/queries/GetArticle.query';
import {
  articleCategoriesRequestConfig,
  GetArticleCategoriesResult,
  GetArticleCategoriesVariables,
} from 'graphql/queries/GetArticleCategories.query';
import {
  similarArticlesRequestConfig,
  GetSimilarArticlesResult,
  GetSimilarArticlesVariables,
} from 'graphql/queries/GetSimilarArticles.query';
import graphQLRequest from 'utils/graphQLRequest';
import combineRequests, { RequestConfig } from 'utils/combineRequest';
import handleError from 'utils/handleError';
import handleNotFoundError from 'utils/handleNotFoundError';
import { startTransaction } from 'utils/startTransaction';
import omit from 'utils/omit';
import mapSimpleVariant from 'utils/mapSimpleVariant';
import { captureException } from 'utils/captureException';

const { routes } = config;

const serverRequests: RequestConfig[] = [...pageLayoutServerRequests, articleRequestConfig];

type Result = GetPageLayoutResult & {
  article: ArticleType;
  similarArticles: SimilarArticle[];
};

export const getServerSideProps: GetServerSideProps<Result> = withShopContext(
  withDescriptionData(
    async ({ res, params, query }, { client, shopId }) => {
      const name = params?.name as string;
      const isPreview = query?.preview === 'true';

      const transaction = startTransaction({ op: 'server.requests', name: routes.article.name.href });
      const articlesDataSpan = transaction.startChild({ op: 'combined-data' });
      const { data: combinedData } = await graphQLRequest<GetPageLayoutResult & GetArticleResult, GetArticleVariables>(
        client,
        combineRequests(serverRequests),
        { variables: { name } }
      );
      articlesDataSpan.finish();

      if (!combinedData) {
        transaction.finish();
        return handleError(res, 500);
      }

      const [article] = combinedData.articles;

      if (!article) {
        transaction.finish();
        return handleNotFoundError(client, res, shopId);
      }

      if ((article.isHidden || new Date(article.publishedAt || 0) > new Date()) && !isPreview) {
        transaction.finish();
        return handleError(res, 503);
      }

      const tag = article.tags[0]?.articleTag;
      const { category } = tag || {};

      if (!tag) {
        captureException(new Error(`Article without tag (name: ${name})`));
      }

      const secondServerRequests: RequestConfig[] = [articleCategoriesRequestConfig, similarArticlesRequestConfig];
      const secondVariables: GetArticleCategoriesVariables & GetSimilarArticlesVariables = {
        category: category?.slug || '',
        tag: tag?.slug || '',
        name,
      };

      const secondCombinedDataSpan = transaction.startChild({ op: 'second-combined-data' });
      const { data: secondCombinedData } = await graphQLRequest<
        GetArticleCategoriesResult & GetSimilarArticlesResult,
        GetArticleCategoriesVariables & GetSimilarArticlesVariables
      >(client, combineRequests(secondServerRequests), {
        variables: secondVariables,
      });
      secondCombinedDataSpan.finish();

      transaction.finish();

      return {
        props: {
          ...omit(combinedData, ['articles']),
          similarArticles: secondCombinedData?.similarArticles || [],
          article,
          breadcrumbs: getBreadcrumbs(
            secondCombinedData?.allArticlesCategories || [],
            category?.tags || [],
            category,
            tag,
            article.seoData?.header
          ),
        },
      };
    },
    {
      descriptionsGetter: ({ article }) => [article.seoData?.description],
      propsMapper: ({ article, ...rest }) => ({
        ...rest,
        article: {
          ...article,
          recommendedVariants: (
            article.recommendedVariants as unknown as { id: number; variant: SimpleVariantFragmentType }[]
          )
            .slice(0, config.articleRecommededVariantsLimit)
            .map(({ variant }) => mapSimpleVariant(variant)),
        },
      }),
    }
  )
);

const Article = ({ article, similarArticles }: InferGetServerSidePropsType<typeof getServerSideProps>): JSX.Element => (
  <ArticlePage {...article} similarArticles={similarArticles} />
);

export default withPageLayout(Article, {
  showBreadcrumb: true,
  breadcrumbVariant: 'articles',
  breadcrumbLinkVariant: 'real',
});
